/**
 * Validador de CNPJ conforme regra do Serpro (CNPJ alfanumérico).
 * Um único algoritmo valida tanto CNPJ somente numérico (14 dígitos) quanto
 * CNPJ alfanumérico (12 caracteres A-Z/0-9 + 2 dígitos verificadores).
 * Referência: calculodvcnpjalfanaumerico.pdf (Serpro)
 */

const TAMANHO_BASE = 12;
const TAMANHO_COM_DV = 14;
const VALOR_BASE_ASCII = 48; // '0'.charCodeAt(0)
/** Pesos para cálculo dos DVs: 13 posições (12 da base + 1º DV). Ordem usada: 1º DV usa índices 1..12, 2º DV usa 0..12. */
const PESOS_DV: number[] = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const REGEX_MASCARA = /[.\/-]/g;
const REGEX_BASE_VALIDA = /^[A-Z0-9]{12}$/;
const REGEX_CNPJ_COMPLETO = /^[A-Z0-9]{12}[0-9]{2}$/;
/** Base ou CNPJ "zerado" (todos 0) é inválido. Para base alfanumérica, consideramos inválido se todos os caracteres são 0. */
const REGEX_ZERADO = /^0+$/;

export class CNPJValidator {

  /**
   * Remove caracteres de formatação (pontos, barra, hífen).
   * Converte para maiúsculas para padronizar letras.
   */
  static normalize(cnpj: string): string {
    if (cnpj == null || typeof cnpj !== 'string') return '';
    return cnpj.trim().replace(REGEX_MASCARA, '').toUpperCase();
  }

  /**
   * Valor do caractere para o cálculo do DV (ASCII - 48).
   * Dígitos '0'..'9' -> 0..9; letras 'A'..'Z' -> 17..42.
   */
  static getValorCaracter(c: string): number {
    return c.charCodeAt(0) - VALOR_BASE_ASCII;
  }

  /**
   * Calcula o dígito verificador (um dígito) para uma sequência de caracteres.
   * @param sequencia 12 ou 13 caracteres (base ou base + 1º DV)
   * @param inicioPeso índice inicial no array PESOS_DV (0 para 13 chars, 1 para 12 chars)
   */
  static calculaUmDV(sequencia: string, inicioPeso: number): number {
    let soma = 0;
    for (let i = 0; i < sequencia.length; i++) {
      soma += this.getValorCaracter(sequencia[i]) * PESOS_DV[inicioPeso + i];
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  }

  /**
   * Calcula os dois dígitos verificadores a partir da base de 12 caracteres.
   * @param base 12 caracteres alfanuméricos [A-Z0-9]
   * @returns string de 2 dígitos (1º e 2º DV)
   */
  static calculateDV(base: string): string {
    const normalizada = this.normalize(base);
    if (!REGEX_BASE_VALIDA.test(normalizada) || REGEX_ZERADO.test(normalizada)) {
      throw new Error('CNPJ base inválida para cálculo do DV: deve ter 12 caracteres alfanuméricos e não ser todos zeros.');
    }
    const dv1 = this.calculaUmDV(normalizada, 1);
    const dv2 = this.calculaUmDV(normalizada + dv1, 0);
    return `${dv1}${dv2}`;
  }

  /**
   * Valida o CNPJ completo (12 caracteres base + 2 DVs) com o algoritmo único do Serpro.
   * Atende tanto CNPJ somente numérico quanto alfanumérico.
   */
  static isValid(cnpj: string): boolean {
    const normalizada = this.normalize(cnpj);
    if (normalizada.length !== TAMANHO_COM_DV) return false;
    if (!REGEX_CNPJ_COMPLETO.test(normalizada)) return false;
    const baseZerada = normalizada.substring(0, TAMANHO_BASE);
    if (REGEX_ZERADO.test(baseZerada)) return false;

    const dvInformado = normalizada.substring(TAMANHO_BASE);
    let dvCalculado: string;
    try {
      dvCalculado = this.calculateDV(baseZerada);
    } catch {
      return false;
    }
    return dvInformado === dvCalculado;
  }

  /**
   * Formata o CNPJ para exibição: AA.AAA.AAA/AAAA-DD
   * Preserva letras e números. Entrada deve estar normalizada ou com máscara.
   */
  static format(cnpj: string): string {
    const normalizada = this.normalize(cnpj);
    if (normalizada.length !== TAMANHO_COM_DV) return cnpj != null ? String(cnpj).trim() : '';
    return normalizada.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
  }
}
